import qs from 'qs';
import { StrapiResponse } from 'src/types/http';
import { Indicator } from 'src/types/indicator';
import {
  AlertConfiguration,
  IndicatorAlertConfiguration,
} from 'src/types/indicator-alert-configuration';
import {
  BaseIndicator,
  IndicatorsResponse,
  OperationCompositionItem,
  OperationViewMetadata,
  Wallet,
  WalletCard,
  WalletFund,
} from 'src/types/wallet';
import { SharedWalletUser } from 'src/types/wallet-monitor';

import { api } from '../../api/api';
import {
  WalletInvitation,
  WalletInvitationLinkStatus,
} from '../invite-monitor';

export type CreateOrUpdateWalletWithFunds = {
  name: string;
  description: string;
  startDate: Date | null;
  funds: {
    operation: number;
    quotas: {
      class: string;
      series: string;
      amount: number;
    }[];
  }[];
};

export type UpdateWalletFundPayload = {
  operation: number;
  useAlertDefault: boolean;
  parentWalletFundId?: number;
  compositionRole: import('src/types/wallet').WalletFundCompositionRole | null;
  quotas: {
    class: string;
    series: string | number;
    amount: number;
  }[];
};

export type UpdateWalletWithFundsPayload = {
  walletId: string;
  funds: UpdateWalletFundPayload[];
};

export const getAllWallets = async ({
  name,
  sort,
  page,
  pageSize,
}: {
  name?: string;
  sort?: string[];
  page?: number;
  pageSize?: number;
}): Promise<StrapiResponse<WalletCard[]>> => {
  const query = qs.stringify(
    {
      filters: {
        name,
      },
      sort: sort,
      pagination: {
        page,
        pageSize,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get(`/wallet/getAllWallets?${query}`);

  return data;
};

export const getWalletById = async (id: string): Promise<Wallet | null> => {
  const query = qs.stringify({
    fields: [
      'id',
      'name',
      'description',
      'startDate',
      'status',
      'acceptedDefaultAlertsConfigs',
      'defaultAlertsConfigsAceeptedAt',
      'purpose',
    ],
    populate: {
      owner: {
        fields: ['id', 'name', 'email', 'fullName'],
      },
      walletFunds: {
        fields: [
          'id',
          'operation',
          'useAlertDefault',
          'compositionRole',
          'quotas',
        ],
        populate: {
          operation: {
            fields: ['id', 'hashId', 'name', 'cnpj'],
          },
          quotas: true,
          parentWalletFund: {
            fields: ['id'],
            populate: {
              operation: {
                fields: ['id', 'hashId', 'name', 'cnpj'],
              },
            },
          },
        },
      },
    },
  });

  const { data } = await api.get(`/wallets/${id}?${query}`);
  return data?.data || null;
};

export const getSimpleWalletById = async (
  id: string,
): Promise<Wallet | null> => {
  const { data } = await api.get(`/wallet/${id}/simplified`);
  return data?.data || null;
};

export const getWalletFundById = async (
  id: string,
): Promise<WalletFund[] | null> => {
  const query = qs.stringify({
    filters: {
      id: {
        $eq: id,
      },
    },
    populate: {
      operation: {
        fields: ['id', 'hashId', 'name', 'cnpj'],
      },
    },
  });

  const { data } = await api.get(`/wallet-funds?${query}`);
  return data?.data || null;
};

export const findWalletByName = async (
  name: string,
  ownerId: number,
): Promise<Wallet[]> => {
  const query = qs.stringify(
    {
      fields: ['id', 'name'],
      sort: ['name:asc'],
      filters: {
        name: {
          $containsi: name,
        },
        owner: ownerId,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/wallets?${query}`);
  return data.data;
};

export const checkWalletNameAvailability = async (
  name: string,
  ownerId: number,
) => {
  const query = qs.stringify(
    {
      fields: ['id', 'name'],
      sort: ['name:asc'],
      filters: {
        name: {
          $eq: name,
        },
        owner: ownerId,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );

  const { data } = await api.get(`/wallets?${query}`);
  return data.data;
};

export const getWalletSummaryInfo = async () => {
  const { data } = await api.get('/wallet/summary-info');
  return data;
};

export async function getWalletFundClasses(cnpj: string) {
  const query = qs.stringify({
    document: cnpj,
  });

  const { data } = await api.get(
    `/metabase-api/operation-sub-classes?${query}`,
  );

  return data as {
    identifier: number;
    fidc: string;
    cnpj: string;
    subclasse: string;
    serie: string | null;
  }[];
}

export const getWalletFundsWarningSummary = async (
  walletId: string | undefined,
  date?: string,
) => {
  const query = qs.stringify({
    walletId,
    date,
  });

  const { data } = await api.get(`/wallet-funds/summary-info?${query}`);
  return data;
};

export async function createWalletWithFunds(
  wallet: CreateOrUpdateWalletWithFunds,
) {
  const { data } = await api.post('/wallet/create-with-funds', wallet);
  return data;
}

export async function createWalletWithFundsForOwner(
  wallet: CreateOrUpdateWalletWithFunds,
  ownerId: string,
) {
  const { data } = await api.post('/wallet/create-with-funds-for-owner', {
    wallet,
    ownerId: parseInt(ownerId),
  });
  return data;
}

export async function updateWalletWithFunds(
  walletId: string,
  wallet: UpdateWalletWithFundsPayload,
) {
  const { data } = await api.put(`/wallet/${walletId}/update-with-funds`, {
    data: {
      wallet,
    },
  });
  return data;
}

export async function updateWalletWithFundsForOwner(
  walletId: string,
  wallet: UpdateWalletWithFundsPayload,
  ownerId: string,
) {
  const { walletId: _payloadWalletId, ...walletData } = wallet;

  const { data } = await api.put(
    `/wallet/${walletId}/update-with-funds-for-owner`,
    {
      data: {
        ownerId: parseInt(ownerId),
        wallet: {
          ...walletData,
          walletId: parseInt(walletId),
        },
      },
    },
  );
  return data;
}

export async function getFundsCompositionByOperationId(walletId: string) {
  const query = qs.stringify({
    walletId,
  });
  const { data } = await api.get<
    Array<{
      product: string;
      operations: number;
    }>
  >(`/wallet/get-wallet-composition?${query}`);
  return data;
}

export async function getIndicatorsColumns(
  walletId: number,
  valueType?: 'percentage' | 'amount',
  market?: string,
) {
  const query = qs.stringify(
    {
      market,
      valueType,
    },
    { encodeValuesOnly: true },
  );
  const { data: response } = await api.get(
    `/wallet/${walletId}/indicators-columns?${query}`,
  );
  return response;
}

export async function getIndicators() {
  const { data } = await api.get<StrapiResponse<Indicator[]>>('/indicators');
  return data.data;
}

export async function getFundIndicators(
  walletId: number,
  date?: string,
): Promise<IndicatorsResponse[]> {
  const query = qs.stringify({ walletId, date });
  const { data } = await api.get<IndicatorsResponse[]>(
    `/indicators/wallet-operations-indicators?${query}`,
  );
  return data;
}

export const updateWalletName = async (walletId: string, name: string) => {
  const { data } = await api.put(`/wallets/${walletId}`, {
    data: {
      name,
    },
  });
  return data;
};

export async function getAlertIndicatorsByWalletAndFundId(
  walletId: number,
  fundId: number,
): Promise<Array<IndicatorAlertConfiguration>> {
  const query = qs.stringify({ walletId, fundId });

  const { data } = await api.get(
    `/indicator-alert-configurations/get-alert-indicators-by-wallet-and-fund-id?${query}`,
  );
  return data;
}

export async function getOwnerDefaultIndicatorsAlertConfigurations() {
  const { data } = await api.get(
    '/indicator-alert-configurations/get-alert-indicators-by-owner',
  );
  return data;
}

export async function getWalletDefaultIndicatorsAlertConfigurations(
  walletId: number,
) {
  const { data } = await api.get(
    `/indicator-alert-configurations/get-alert-indicators-by-wallet/${walletId}`,
  );
  return data;
}

export async function getIndicatorsColumnsByMarketAndValueType(
  market: string,
  valueType: 'percentage' | 'amount',
) {
  const query = qs.stringify({
    market,
    valueType,
  });
  const { data: response } = await api.get(
    `/indicators/get-indicators-columns?${query}`,
  );
  return response;
}

export const handleIndicatorsHeadersByIndicatorsResponse = (
  data: BaseIndicator | BaseIndicator[],
  type: 'Porcentagem' | 'Montante',
) => {
  if (!data) {
    throw new Error('Data is required');
  }

  const percentageKeysPreSuffix = ['delta', 'sobre', 'pct'];
  const excludedKeys = [
    'report_id',
    'cnpj',
    'document_date',
    'version_date',
    'pl_cotas_status',
  ];

  const sampleData =
    Array.isArray(data) && data.length > 0 ? data[0] : (data as BaseIndicator);

  const filteredKeys = Object.keys(sampleData).filter((key) => {
    if (excludedKeys.includes(key)) {
      return false;
    }

    if (type === 'Porcentagem') {
      return percentageKeysPreSuffix.some((suffix) => key.includes(suffix));
    } else {
      return !percentageKeysPreSuffix.some((suffix) => key.includes(suffix));
    }
  });

  const headers = filteredKeys.map((key) => ({
    label: key
      .replaceAll(/delta/g, '&#916;')
      .replaceAll(/pct/g, '')
      .replaceAll(/_/g, ' ')
      .toLocaleUpperCase()
      .trim(),
    key,
  }));

  return [
    { label: 'OPERAÇÃO', key: 'operacao' },
    ...headers,
    { label: '', key: 'alertas' },
  ];
};

export type MetaDataKeys = 'ownerId' | 'walletId' | 'fundId' | 'quotaId';

export type BaseBulkCRUDAlertIndicatorConfigurations<
  TMetaData extends Partial<Record<MetaDataKeys, number | string>>,
> = {
  metadata: TMetaData;
  toCreate: Array<AlertConfiguration>;
  toUpdate: Array<AlertConfiguration>;
  toDelete: Array<AlertConfiguration>;
};

export type BulkCreateUpdateOrDeleteAlertIndicatorConfigurationsDefault =
  BaseBulkCRUDAlertIndicatorConfigurations<{
    ownerId: number;
  }>;

export type BulkCreateUpdateOrDeleteAlertIndicatorConfigurationsWalletDefault =
  BaseBulkCRUDAlertIndicatorConfigurations<{
    walletId: number;
  }>;

export type BulkCreateUpdateOrDeleteAlertIndicatorConfigurations =
  BaseBulkCRUDAlertIndicatorConfigurations<{
    walletId: string;
    fundId: string;
  }>;

export async function bulkCreateUpdateOrDeleteAlertIndicatorConfigurations({
  metadata,
  toCreate,
  toUpdate,
  toDelete,
}: BulkCreateUpdateOrDeleteAlertIndicatorConfigurations) {
  const { data } = await api.post(
    '/indicator-alert-configurations/bulk-create-update-or-delete',
    {
      metadata,
      toCreate,
      toUpdate,
      toDelete,
    },
  );
  return data;
}

export async function bulkCreateUpdateOrDeleteAlertIndicatorConfigurationsDefault({
  metadata,
  toCreate,
  toUpdate,
  toDelete,
}: BulkCreateUpdateOrDeleteAlertIndicatorConfigurationsDefault) {
  const { data } = await api.post(
    '/indicator-alert-configurations/bulk-create-update-or-delete-default',
    {
      metadata,
      toCreate,
      toUpdate,
      toDelete,
    },
  );
  return data;
}

export async function bulkCreateUpdateOrDeleteAlertIndicatorConfigurationsWalletDefault({
  metadata,
  toCreate,
  toUpdate,
  toDelete,
}: BulkCreateUpdateOrDeleteAlertIndicatorConfigurationsWalletDefault) {
  const { data } = await api.post(
    '/indicator-alert-configurations/bulk-create-update-or-delete-wallet-default',
    {
      metadata,
      toCreate,
      toUpdate,
      toDelete,
    },
  );
  return data;
}

export async function getIndicatorDataHistory(
  document: string,
  targetDate: string,
  months?: number,
) {
  const query = qs.stringify(
    { document, date: targetDate, months },
    { encodeValuesOnly: true },
  );
  const { data } = await api.get(`/indicators/indicator-data-history?${query}`);
  return data as BaseIndicator[];
}

export async function deleteWallet(walletId: number) {
  if (!walletId) {
    throw new Error('Wallet ID is required');
  }

  const { data } = await api.delete(
    `/wallet/delete-wallet-and-relations/${walletId}`,
  );

  return data;
}

export async function updateWalletFundUseAlertDefaultData({
  walletFundId,
  walletDefaultAlertData,
}: {
  walletFundId: number;
  walletDefaultAlertData: boolean;
}) {
  if (!walletFundId) {
    throw new Error('Wallet Fund ID is required');
  }

  if (typeof walletDefaultAlertData !== 'boolean') {
    throw new Error('Wallet Default Alert Data must be a valid boolean');
  }

  const { data } = await api.put(`/wallet-funds/${walletFundId}`, {
    data: {
      useAlertDefault: walletDefaultAlertData,
    },
  });
  return data;
}

export async function createDefaultAlertIndicatorConfigurations() {
  const { data } = await api.post(
    '/indicator-alert-configurations/create-default-alert-configurations-for-user',
  );
  return data;
}

export async function getUserDefaultAlertIndicatorConfigurations() {
  const { data } = await api.get(
    '/indicator-alert-configurations/get-alert-indicators-by-owner',
  );
  return data;
}

export async function acceptDefaultAlertIndicatorConfigurations(
  walletId: number,
) {
  const { data } = await api.post(
    '/wallet/accept-default-alerts-configurations',
    {
      walletId,
      acceptedDefaultAlertsConfigs: true,
    },
  );
  return data;
}

export async function getSharedWalletUsers(walletId: number) {
  const query = qs.stringify(
    {
      walletId,
      populate: {
        guest: true,
        wallet: true,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get<StrapiResponse<SharedWalletUser[]>>(
    `/wallet-monitor-shared-wallet/get-shared-wallet-users?${query}`,
  );
  return data;
}

export async function shareWallet(walletId: number, email: string) {
  const { data } = await api.post(
    '/wallet-monitor-shared-wallet/share-wallet',
    {
      walletId,
      email,
    },
  );
  return data;
}

export async function removeSharedWallet(walletId: number, email: string) {
  const { data } = await api.post(
    '/wallet-monitor-shared-wallet/remove-shared-wallet',
    {
      walletId,
      email,
    },
  );
  return data;
}

export async function renewSharedWallet(walletId: number, email: string) {
  const { data } = await api.post(
    '/wallet-monitor-shared-wallet/renew-shared-wallet',
    {
      walletId,
      email,
    },
  );
  return data;
}

export const getAllSharedWallets = async ({
  name,
  sort,
  page,
  pageSize,
}: {
  name?: string;
  sort?: string[];
  page?: number;
  pageSize?: number;
}) => {
  const query = qs.stringify(
    {
      filters: {
        name,
      },
      sort: sort,
      pagination: {
        page,
        pageSize,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get<StrapiResponse<WalletCard[]>>(
    `/wallet-monitor-shared-wallet/get-shared-wallets?${query}`,
  );

  return data;
};

export const getWalletsByGuestOwner = async ({
  ownerId,
  status,
  filters,
}: {
  ownerId: string;
  status?: 'accepted' | 'pending' | 'rejected';
  filters?: {
    name?: string;
    sort?: string[];
    page?: number;
    pageSize?: number;
  };
}) => {
  const query = qs.stringify({
    ownerId,
    status,
    ...filters,
  });
  const { data } = await api.get(`/wallet/get-wallets-by-guest-owner?${query}`);

  return data;
};

export const getGuestWalletsSummaryInfo = async ({
  ownerId,
  status,
}: {
  ownerId: string;
  status?: 'accepted' | 'pending' | 'rejected';
}) => {
  const query = qs.stringify({
    ownerId,
    status,
  });
  const { data } = await api.get(
    `/wallet/get-guest-wallets-summary-info?${query}`,
  );
  return data;
};

export const getGuestWalletByOwnerAndWalletId = async ({
  ownerId,
  walletId,
}: {
  ownerId: string;
  walletId: string;
}) => {
  const query = qs.stringify({
    ownerId,
    walletId,
  });
  const { data } = await api.get<{
    id: number;
    status: WalletInvitationLinkStatus;
    createdAt: string;
    updatedAt: string;
    wallet: Wallet;
    invitation: WalletInvitation;
  }>(`/wallet/get-wallet-by-owner-wallet-id?${query}`);
  return data || null;
};

export const sendRequestPendingWallet = async (walletId: string) => {
  const { data } = await api.post(
    '/wallet-invitations/send-wallet-approval-request',
    {
      data: {
        walletId,
      },
    },
  );

  return data;
};

export const getPendingWalletsByOwnerId = async ({
  name,
  sort,
  page,
  pageSize,
}: {
  name?: string;
  sort?: string[];
  page?: number;
  pageSize?: number;
}) => {
  const query = qs.stringify(
    {
      filters: {
        name,
      },
      sort: sort,
      pagination: {
        page,
        pageSize,
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  const { data } = await api.get(
    `/wallet/get-pending-wallets-by-owner-id?${query}`,
  );
  return data?.data;
};

export const approveOrRejectPendingWallet = async (
  walletId: string,
  status: 'accepted' | 'rejected',
) => {
  const { data } = await api.put(
    `/wallet/${walletId}/approve-or-reject-pending-wallet`,
    {
      data: {
        status,
      },
    },
  );
  return data;
};

export type UserWalletWithFundsCount = {
  id: number;
  name: string;
  fundsCount: number;
  isFundMonitored: boolean;
  /** Roots + FIC children; use parentWalletFund === null for limit counting. */
  walletFunds?: Array<{
    operation: {
      cnpj: string | null;
      hashId?: string | null;
    };
    parentWalletFund?: { id: number } | null;
  }>;
};

export type FicChildOperation = {
  id: number;
  hashId: string;
  name: string;
  cnpj: string;
  slug: string;
  market: { name: string } | null;
};

export type FicChildrenResponse = {
  operation: {
    id: number;
    hashId: string;
    name: string;
    cnpj: string;
  };
  isFic: boolean;
  children: FicChildOperation[];
};

export type FicChildrenBatchStatus =
  | 'fic'
  | 'not_fic'
  | 'not_found'
  | 'composition_error';

export type FicChildrenBatchResult = {
  requestedOperationId: string;
  status: FicChildrenBatchStatus;
  operation?: FicChildrenResponse['operation'];
  children?: FicChildOperation[];
};

export type FicChildrenBatchResponse = {
  results: FicChildrenBatchResult[];
  meta: { requestedCount: number; uniqueCount: number };
};

export async function getOperationFicChildren(
  operationId: string,
): Promise<FicChildrenResponse> {
  const { data } = await api.get<FicChildrenResponse>(
    `/wallet/operations/${operationId}/fic-children`,
  );
  return data;
}

export async function getOperationsFicChildrenBatch(
  operationIds: string[],
): Promise<FicChildrenBatchResponse> {
  const { data } = await api.post<FicChildrenBatchResponse>(
    '/wallet/operations/fic-children/batch',
    { operationIds },
  );
  return data;
}

export async function getUserWalletsWithFundsCount(
  hashId?: string,
): Promise<UserWalletWithFundsCount[]> {
  const { data } = await api.get<UserWalletWithFundsCount[]>(
    `/wallet/get-user-wallets-with-funds-count/${hashId}`,
  );
  return data ?? [];
}

export async function addFundToWallet(
  walletId: number,
  operationId: string,
  childOperationIds?: string[],
) {
  const { data } = await api.post(`/wallet/${walletId}/add-fund`, {
    operationId,
    ...(childOperationIds !== undefined ? { childOperationIds } : {}),
  });
  return data;
}

export type OperationMonitoredWallet = {
  id: number;
  name: string;
};

export async function getOperationMonitoredWallets(
  operationId: string,
): Promise<OperationMonitoredWallet[]> {
  const { data } = await api.get<{ wallets: OperationMonitoredWallet[] }>(
    `/wallet/operations/${operationId}/is-monitored`,
  );
  return data?.wallets ?? [];
}

export async function createMonitoringFundWithWallet(
  walletName: string,
  operationId: string,
  childOperationIds?: string[],
) {
  const { data } = await api.post(
    '/wallet/create-monitoring-fund-with-wallet',
    {
      walletName,
      operationId,
      ...(childOperationIds !== undefined ? { childOperationIds } : {}),
    },
  );
  return data;
}

export type FollowFundResponse = {
  walletId: number;
  success: boolean;
  error: string;
};

export async function followFund(
  operationId: string,
): Promise<FollowFundResponse> {
  const { data } = await api.post<FollowFundResponse>('/wallet/follow-fund', {
    operationId,
  });
  return data;
}

export async function unfollowFund(
  operationId: string,
): Promise<FollowFundResponse> {
  const { data } = await api.post<FollowFundResponse>('/wallet/unfollow-fund', {
    operationId,
  });
  return data;
}

export type FollowedFund = WalletFund & {
  /**
   * FIC de FIDC: quando verdadeiro, a linha pode ser expandida (colapsável)
   * para exibir a composição do fundo. A estrutura definitiva ainda não foi
   * definida no backend; o booleano permite preparar a UI desde já.
   */
  isFic?: boolean;
  /** Composição do FIC. Ainda não populada pelo backend. */
  composition?: FollowedFund[];
};

/**
 * Carteira especial (`purpose: 'followed_funds'`) que agrupa os fundos seguidos
 * pelo usuário. Reaproveita a mesma estrutura de uma carteira comum, por isso a
 * aba "Seguindo" se comporta exatamente como a página da carteira: a tabela de
 * indicadores é consultada pelo `id` desta carteira.
 */
export type FollowedFundsWallet = Wallet & {
  purpose?: string;
  walletFunds: FollowedFund[];
};

export type FollowedFundsResponse = {
  wallet: FollowedFundsWallet | null;
  followFunds?: boolean;
  followQuantity?: number | null;
  followCount?: number;
  limits?: MonitorLimitsResponse['limits'];
  usage?: MonitorLimitsResponse['usage'];
};

export type MonitorLimitsResponse = {
  limits: {
    walletQuantity: number | null;
    fundsQuantity: number | null;
    followFunds: boolean;
    plan?: string;
    level?: string;
    source?: string;
  };
  usage: {
    wallets: number;
  };
  followFunds: boolean;
};

export async function getFollowedFunds(): Promise<FollowedFundsResponse> {
  const { data } = await api.get<{
    data: FollowedFundsWallet | null;
    followFunds?: boolean;
    followQuantity?: number | null;
    followCount?: number;
    limits?: MonitorLimitsResponse['limits'];
    usage?: MonitorLimitsResponse['usage'];
  }>('/wallet/followed-funds');

  return {
    wallet: data?.data ?? null,
    followFunds: data?.followFunds,
    followQuantity: data?.followQuantity,
    followCount: data?.followCount,
    limits: data?.limits,
    usage: data?.usage,
  };
}

export async function getMonitorLimits(): Promise<MonitorLimitsResponse> {
  const { data } = await api.get<MonitorLimitsResponse>(
    '/wallet/monitor-limits',
  );
  return data;
}

export const cancelSentWalletApprovalRequest = async ({
  walletId,
  status,
}: {
  walletId: string;
  status: Exclude<
    WalletInvitationLinkStatus,
    'draft' | 'accepted' | 'rejected'
  >;
}) => {
  const { data } = await api.put(
    `/wallet/${walletId}/cancel-pending-wallet-to-approval-reject-action`,
    {
      data: {
        status,
      },
    },
  );
  return data;
};

export async function getOperationViewMetadata(
  operationId: string,
  date?: string,
): Promise<OperationViewMetadata> {
  const query = qs.stringify({ operationId, date });
  const { data } = await api.get<OperationViewMetadata>(
    `/wallet/get-monitor-view?${query}`,
  );
  return data;
}

export async function getOperationComposition(
  operationId: string,
): Promise<OperationCompositionItem[]> {
  const query = qs.stringify({ operationId });
  const { data } = await api.get<OperationCompositionItem[]>(
    `/wallet/get-operation-composition?${query}`,
  );
  return data;
}

export async function getOperationIndicators(
  operationId: string,
  date?: string,
): Promise<IndicatorsResponse[]> {
  const query = qs.stringify({ operationId, date });
  const { data } = await api.get<IndicatorsResponse[]>(
    `/indicators/operation-indicators?${query}`,
  );
  return data;
}
